export function format(value, format, utc = false) {
  const jsdate = new Date(value);

  // Day and month word stems
  const WORDS = [
    "Sun",
    "Mon",
    "Tues",
    "Wednes",
    "Thurs",
    "Fri",
    "Satur",
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Matches an optional backslash (escape) and the following single character
  const TOKEN_REGEX = /\\?(.?)/gi;

  const zeroPad = (num, width) => {
    let out = String(num);
    while (out.length < width) out = `0${out}`;
    return out;
  };

  const tokens = {
    // Day
    d() {
      // Day of month w/leading 0; 01..31
      return zeroPad(tokens.j(), 2);
    },
    D() {
      // Shorthand day name; Mon...Sun
      return tokens.l().slice(0, 3);
    },
    j() {
      // Day of month; 1..31
      return utc ? jsdate.getUTCDate() : jsdate.getDate();
    },
    l() {
      // Full day name; Monday...Sunday
      return `${WORDS[tokens.w()]}day`;
    },
    N() {
      // ISO-8601 day of week; 1[Mon]..7[Sun]
      return tokens.w() || 7;
    },
    S() {
      // Ordinal suffix for day of month; st, nd, rd, th
      const day = tokens.j();
      let lastDigit = day % 10;
      // Handle 11th–13th as 'th'
      if (lastDigit <= 3 && parseInt(((day % 100) / 10).toString(), 10) === 1) {
        lastDigit = 0;
      }
      return ["st", "nd", "rd"][lastDigit - 1] || "th";
    },
    w() {
      // Day of week; 0[Sun]..6[Sat]
      return utc ? jsdate.getUTCDay() : jsdate.getDay();
    },
    z() {
      // Day of year; 0..365
      const current = new Date(tokens.Y(), tokens.n() - 1, tokens.j());
      const startOfYear = new Date(tokens.Y(), 0, 1);
      return Math.round((Number(current) - Number(startOfYear)) / 864e5);
    },

    // Week
    W() {
      // ISO-8601 week number
      const targetThursday = new Date(tokens.Y(), tokens.n() - 1, tokens.j() - tokens.N() + 3);
      const firstThursday =
        utc
          ? new Date(targetThursday.getUTCFullYear(), 0, 4)
          : new Date(targetThursday.getFullYear(), 0, 4);
      const weekNumber = 1 + Math.round((Number(targetThursday) - Number(firstThursday)) / 864e5 / 7);
      return zeroPad(weekNumber, 2);
    },

    // Month
    F() {
      // Full month name; January...December
      return WORDS[6 + tokens.n()];
    },
    m() {
      // Month w/leading 0; 01...12
      return zeroPad(tokens.n(), 2);
    },
    M() {
      // Shorthand month name; Jan...Dec
      return tokens.F().slice(0, 3);
    },
    n() {
      // Month; 1...12
      return (utc ? jsdate.getUTCMonth() : jsdate.getMonth()) + 1;
    },
    t() {
      // Days in month; 28...31
      const lastDay = new Date(tokens.Y(), tokens.n(), 0);
      return utc ? lastDay.getUTCDate() : lastDay.getDate();
    },

    // Year
    L() {
      // Is leap year?; 0 or 1
      const year = tokens.Y();
      return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 1 : 0;
    },
    o() {
      // ISO-8601 year
      const month = tokens.n();
      const isoWeek = parseInt(tokens.W(), 10);
      const year = tokens.Y();
      return year + (month === 12 && isoWeek < 9 ? 1 : month === 1 && isoWeek > 9 ? -1 : 0);
    },
    Y() {
      // Full year
      return utc ? jsdate.getUTCFullYear() : jsdate.getFullYear();
    },
    y() {
      // Last two digits of year; 00...99
      return tokens.Y().toString().slice(-2);
    },

    // Time
    a() {
      // am or pm
      const hour = utc ? jsdate.getUTCHours() : jsdate.getHours();
      return hour > 11 ? "pm" : "am";
    },
    A() {
      // AM or PM
      return tokens.a().toUpperCase();
    },
    B() {
      // Swatch Internet time; 000..999
      const secondsUTC =
        jsdate.getUTCHours() * 3600 +
        jsdate.getUTCMinutes() * 60 +
        jsdate.getUTCSeconds();
      // Add one hour to align with Biel Mean Time (UTC+1) and scale
      const beats = Math.floor((secondsUTC + 3600) / 86.4) % 1000;
      return zeroPad(beats, 3);
    },
    g() {
      // 12-Hours; 1..12
      return tokens.G() % 12 || 12;
    },
    G() {
      // 24-Hours; 0..23
      return utc ? jsdate.getUTCHours() : jsdate.getHours();
    },
    h() {
      // 12-Hours w/leading 0; 01..12
      return zeroPad(tokens.g(), 2);
    },
    H() {
      // 24-Hours w/leading 0; 00..23
      return zeroPad(tokens.G(), 2);
    },
    i() {
      // Minutes w/leading 0; 00..59
      const minutes = utc ? jsdate.getUTCMinutes() : jsdate.getMinutes();
      return zeroPad(minutes, 2);
    },
    s() {
      // Seconds w/leading 0; 00..59
      const seconds = utc ? jsdate.getUTCSeconds() : jsdate.getSeconds();
      return zeroPad(seconds, 2);
    },
    u() {
      // Microseconds; 000000-999000
      const ms = utc ? jsdate.getUTCMilliseconds() : jsdate.getMilliseconds();
      return zeroPad(ms * 1000, 6);
    },

    // Timezone
    e() {
      // Timezone identifier (not supported without large lookup)
      throw new Error(
        "Not supported (see source code of date() for timezone on how to add support)"
      );
    },
    I() {
      // DST observed?; 0 or 1
      const janLocal = new Date(tokens.Y(), 0);        // Jan 1 local
      const janUTCms = Date.UTC(tokens.Y(), 0);        // Jan 1 UTC ms
      const julLocal = new Date(tokens.Y(), 6);        // Jul 1 local
      const julUTCms = Date.UTC(tokens.Y(), 6);        // Jul 1 UTC ms
      return Number(janLocal) - janUTCms !== Number(julLocal) - julUTCms ? 1 : 0;
    },
    O() {
      // Difference to GMT in hour format; e.g. +0200
      const tzOffsetMinutes = jsdate.getTimezoneOffset();
      const abs = Math.abs(tzOffsetMinutes);
      const hoursAndMinutes = Math.floor(abs / 60) * 100 + (abs % 60);
      return (tzOffsetMinutes > 0 ? "-" : "+") + zeroPad(hoursAndMinutes, 4);
    },
    P() {
      // Difference to GMT w/colon; e.g. +02:00
      const offset = tokens.O();
      return `${offset.substr(0, 3)}:${offset.substr(3, 2)}`;
    },
    T() {
      // Human-readable zone hint (simplified)
      return utc ? "UTC" : "LOCAL";
    },
    Z() {
      // Timezone offset in seconds (-43200...50400)
      return -jsdate.getTimezoneOffset() * 60;
    },

    // Full Date/Time
    c() {
      // ISO-8601 date.
      return "Y-m-d\\TH:i:sP".replace(TOKEN_REGEX, tokenCallback);
    },
    r() {
      // RFC 2822
      return "D, d M Y H:i:s O".replace(TOKEN_REGEX, tokenCallback);
    },
    U() {
      // Seconds since UNIX epoch
      return (Number(jsdate) / 1000) | 0;
    },
  };

  // Callback used by String.replace; respects escaping with backslash
  function tokenCallback(match, token) {
    // If there was a backslash before the token, return the literal token
    if (match !== token) return token;
    return tokens[token] ? tokens[token]() : token;
  }

  return format.replace(TOKEN_REGEX, tokenCallback);
}